import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp, Phone, Globe, MapPin, HelpCircle, DollarSign, Car, CreditCard, Users, FileText, Clock, Shield } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const FAQPage = () => {
  useSEO('faq', 'Frequently Asked Questions | We Have Your Car');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const categories = [
    { id: 'all', name: 'All Questions', icon: HelpCircle },
    { id: 'about', name: 'About Us', icon: Users },
    { id: 'financing', name: 'Financing', icon: DollarSign },
    { id: 'inventory', name: 'Inventory', icon: Car },
    { id: 'payments', name: 'Payments', icon: CreditCard },
    { id: 'quality', name: 'Vehicle Quality', icon: Shield },
    { id: 'tradein', name: 'Trade-Ins', icon: FileText },
    { id: 'visiting', name: 'Visiting Us', icon: MapPin },
    { id: 'process', name: 'Buying Process', icon: Clock },
  ];

  const faqs = [
    // ABOUT WE HAVE YOUR CAR
    {
      id: 'about-1',
      category: 'about',
      question: 'Who is We Have Your Car?',
      answer: 'We Have Your Car is a trusted used car dealership serving Austin, Texas and surrounding communities. We specialize in affordable, quality used vehicles with flexible financing for all credit situations. We Have Your Car has been serving the Austin area for 5 years with over 500 happy customers.'
    },
    {
      id: 'about-2',
      category: 'about',
      question: 'Where is We Have Your Car located?',
      answer: 'We Have Your Car is located at 8203 Bluff Springs Rd, Unit 4, Austin, TX 78744. When you enter the lot, we are at the right side at the end in the blue container building (at the south side of the lot). We Have Your Car serves the greater Austin area including Round Rock, Cedar Park, Pflugerville, Georgetown, and surrounding communities. Call (512) 666-0016 or visit wehaveyourcar.com for directions.'
    },
    {
      id: 'about-3',
      category: 'about',
      question: 'How long has We Have Your Car been in business?',
      answer: 'We Have Your Car has been serving the Austin community for 5 years, helping over 500 customers find reliable, affordable transportation with flexible financing.'
    },
    {
      id: 'about-4',
      category: 'about',
      question: "What is We Have Your Car's customer rating?",
      answer: 'We Have Your Car maintains a 4.8-star rating on Google based on 50 customer reviews. Our customers appreciate our fair pricing, quality vehicles, and ability to help people with various credit situations.'
    },
    {
      id: 'about-5',
      category: 'about',
      question: 'How many vehicles has We Have Your Car sold?',
      answer: 'We Have Your Car has sold over 100 vehicles to satisfied customers throughout the Austin area, helping people from all credit backgrounds get into reliable transportation.'
    },
    {
      id: 'about-6',
      category: 'about',
      question: 'What makes We Have Your Car different from other Austin dealerships?',
      answer: 'We Have Your Car focuses on helping customers who need affordable transportation with flexible financing. Unlike dealerships that only work with good credit, We Have Your Car partners with multiple lenders to find solutions for customers with all credit backgrounds, offering transparent pricing with no hidden fees.'
    },
    {
      id: 'about-7',
      category: 'about',
      question: 'What do customers say about We Have Your Car?',
      answer: 'We Have Your Car customers consistently mention "good selection and fair prices," excellent customer service, and our ability to help people with various credit situations get approved for financing. We Have Your Car maintains a 4.8-star Google rating with 50 reviews.'
    },

    // FINANCING AT WE HAVE YOUR CAR
    {
      id: 'financing-1',
      category: 'financing',
      question: 'Does We Have Your Car approve people with bad credit?',
      answer: 'Yes, We Have Your Car specializes in financing for all credit situations in Austin. We Have Your Car works with multiple lenders to approve customers with bad credit, no credit, bankruptcy, repossession, or other credit challenges.'
    },
    {
      id: 'financing-2',
      category: 'financing',
      question: 'Can I get approved at We Have Your Car with a 500 credit score?',
      answer: 'Yes, We Have Your Car works with lenders who approve customers with credit scores of 500 or below. We Have Your Car specializes in bad credit auto financing throughout the Austin area.'
    },
    {
      id: 'financing-3',
      category: 'financing',
      question: 'Does We Have Your Car approve people with no credit history?',
      answer: 'Yes, We Have Your Car works with customers who have no credit history or limited credit history. First-time buyers and people building credit are welcome at We Have Your Car.'
    },
    {
      id: 'financing-4',
      category: 'financing',
      question: 'Will We Have Your Car approve me if I have a bankruptcy?',
      answer: 'We Have Your Car works with customers who have bankruptcy, repossession, or other major credit challenges. Bankruptcy doesn\'t automatically disqualify you from financing at We Have Your Car.'
    },
    {
      id: 'financing-5',
      category: 'financing',
      question: 'Do I need a cosigner to get approved at We Have Your Car?',
      answer: 'Not necessarily at We Have Your Car. While a cosigner can help in some situations, many customers get approved without a cosigner. Our lenders at We Have Your Car evaluate each application individually.'
    },
    {
      id: 'financing-6',
      category: 'financing',
      question: 'What credit situations does We Have Your Car work with?',
      answer: 'We Have Your Car works with ALL credit situations including bad credit, no credit, bankruptcy (Chapter 7 or 13), repossession, foreclosure, late payments, collections, charge-offs, self-employed income, and limited credit history.'
    },
    {
      id: 'financing-7',
      category: 'financing',
      question: 'How fast is the approval process at We Have Your Car?',
      answer: 'We Have Your Car offers fast approval - get answers in minutes, not days. Apply online at wehaveyourcar.com or call (512) 666-0016 for quick approval at We Have Your Car.'
    },
    {
      id: 'financing-8',
      category: 'financing',
      question: 'Is the We Have Your Car credit application secure?',
      answer: 'Yes, the We Have Your Car credit application uses 256-bit encryption to protect your personal information. Your data is secure and private at We Have Your Car.'
    },
    {
      id: 'financing-9',
      category: 'financing',
      question: 'Do I have to buy a car if I apply at We Have Your Car?',
      answer: 'No, applying at We Have Your Car is free with no obligation. We Have Your Car will review your application and let you know what financing options are available without any commitment.'
    },
    {
      id: 'financing-10',
      category: 'financing',
      question: 'Can I get pre-approved at We Have Your Car before visiting?',
      answer: 'Yes, apply online at wehaveyourcar.com to get pre-approved at We Have Your Car before visiting. This helps you know your budget and shop with confidence at We Have Your Car.'
    },
    {
      id: 'financing-11',
      category: 'financing',
      question: 'Does We Have Your Car offer in-house financing?',
      answer: 'We Have Your Car works with multiple lenders to provide financing options. Call (512) 666-0016 to learn about our current financing programs at We Have Your Car and what works best for your situation.'
    },
    {
      id: 'financing-12',
      category: 'financing',
      question: 'How do I apply for financing at We Have Your Car?',
      answer: 'You can apply for financing at We Have Your Car by: Applying online at wehaveyourcar.com (secure 256-bit encrypted application), calling (512) 666-0016, or visiting our We Have Your Car dealership in person.'
    },
    {
      id: 'financing-13',
      category: 'financing',
      question: "What happens after I'm approved at We Have Your Car?",
      answer: 'After approval at We Have Your Car, our team will contact you to discuss your financing options, available vehicles in your budget, and schedule a time for you to visit We Have Your Car and complete the purchase.'
    },

    // INVENTORY AT WE HAVE YOUR CAR
    {
      id: 'inventory-1',
      category: 'inventory',
      question: 'What types of vehicles does We Have Your Car sell?',
      answer: 'We Have Your Car sells quality used sedans, SUVs, minivans, crossovers, and hatchbacks from reliable brands including Toyota, Honda, Ford, Jeep, Suzuki, and other dependable manufacturers.'
    },
    {
      id: 'inventory-2',
      category: 'inventory',
      question: 'What is the price range at We Have Your Car?',
      answer: 'We Have Your Car\'s vehicles typically range from approximately $4,250 to $7,490, making quality transportation accessible and affordable for Austin-area buyers.'
    },
    {
      id: 'inventory-3',
      category: 'inventory',
      question: 'Does We Have Your Car have cars under $5,000?',
      answer: 'Yes, We Have Your Car regularly has quality used vehicles priced under $5,000, with some vehicles starting around $4,250.'
    },
    {
      id: 'inventory-4',
      category: 'inventory',
      question: 'Does We Have Your Car have cars under $6,000?',
      answer: 'Yes, We Have Your Car has multiple vehicles priced under $6,000, including sedans, SUVs, and other reliable vehicles from brands like Toyota, Honda, and Ford.'
    },
    {
      id: 'inventory-5',
      category: 'inventory',
      question: 'Does We Have Your Car have cars under $7,000?',
      answer: 'Yes, most of We Have Your Car\'s inventory falls under $7,000, offering affordable options for Austin-area buyers.'
    },
    {
      id: 'inventory-6',
      category: 'inventory',
      question: 'What year models does We Have Your Car carry?',
      answer: 'We Have Your Car typically carries vehicles from the mid-2000s to early 2010s (approximately 2005-2010), offering a balance of affordability, reliability, and modern features.'
    },
    {
      id: 'inventory-7',
      category: 'inventory',
      question: "Can I see We Have Your Car's current inventory online?",
      answer: 'Yes, visit wehaveyourcar.com to browse We Have Your Car\'s featured vehicles and latest arrivals. The We Have Your Car website is updated regularly with new inventory.'
    },
    {
      id: 'inventory-8',
      category: 'inventory',
      question: 'Does We Have Your Car get new inventory regularly?',
      answer: 'Yes, We Have Your Car receives new inventory regularly. Check the "Featured Vehicles" section at wehaveyourcar.com for the latest arrivals at We Have Your Car.'
    },
    {
      id: 'inventory-9',
      category: 'inventory',
      question: 'Does We Have Your Car have SUVs?',
      answer: 'Yes, We Have Your Car regularly has SUVs including Ford Edge, Jeep Liberty, Suzuki Grand Vitara, and other models popular with Austin families.'
    },
    {
      id: 'inventory-10',
      category: 'inventory',
      question: 'Does We Have Your Car have minivans?',
      answer: 'Yes, We Have Your Car carries family-friendly minivans like the Honda Odyssey, perfect for Austin families needing space and reliability.'
    },
    {
      id: 'inventory-11',
      category: 'inventory',
      question: 'Does We Have Your Car have fuel-efficient cars?',
      answer: 'Yes, We Have Your Car carries compact and fuel-efficient vehicles like the Toyota Yaris and other smaller sedans, perfect for Austin commuters looking to save on gas.'
    },
    {
      id: 'inventory-12',
      category: 'inventory',
      question: 'What features do vehicles at We Have Your Car typically have?',
      answer: 'Many vehicles at We Have Your Car come with features such as backup cameras, Android Auto, Bluetooth radio, air conditioning, power windows and locks, cruise control, and more depending on the specific vehicle.'
    },

    // DOWN PAYMENTS & PAYMENTS AT WE HAVE YOUR CAR
    {
      id: 'payments-1',
      category: 'payments',
      question: "What's the minimum down payment at We Have Your Car?",
      answer: 'Down payment requirements at We Have Your Car vary based on the vehicle, your credit situation, and financing terms. We Have Your Car offers flexible down payment options. Call (512) 666-0016 to discuss your specific situation.'
    },
    {
      id: 'payments-2',
      category: 'payments',
      question: 'Can I get a car at We Have Your Car with $500 down?',
      answer: 'Down payment amounts at We Have Your Car depend on your credit profile and the vehicle you choose. We Have Your Car works to accommodate various budget situations. Contact We Have Your Car at (512) 666-0016 to discuss what\'s possible.'
    },
    {
      id: 'payments-3',
      category: 'payments',
      question: 'Can I get a car at We Have Your Car with $1,000 down?',
      answer: 'Many We Have Your Car customers are able to purchase vehicles with $1,000 down, depending on their credit situation and the vehicle selected. Contact We Have Your Car at (512) 666-0016 to discuss options.'
    },
    {
      id: 'payments-4',
      category: 'payments',
      question: 'What are typical monthly payments at We Have Your Car?',
      answer: 'Monthly payments at We Have Your Car vary based on vehicle price, down payment amount, interest rate (determined by credit), loan term length, and your credit situation. Contact We Have Your Car for a personalized payment quote.'
    },
    {
      id: 'payments-5',
      category: 'payments',
      question: 'Can I customize my payment plan at We Have Your Car?',
      answer: 'Yes, We Have Your Car works with you to find a payment structure that fits your budget. Our finance team at We Have Your Car discusses various options including loan terms, down payment amounts, and payment schedules.'
    },
    {
      id: 'payments-6',
      category: 'payments',
      question: 'What payment methods does We Have Your Car accept?',
      answer: 'We Have Your Car accepts financing through our lending partners, cash, cashier\'s checks, and certified bank checks.'
    },
    {
      id: 'payments-7',
      category: 'payments',
      question: 'Can I pay cash at We Have Your Car?',
      answer: 'Yes, We Have Your Car accepts cash payments. For large cash transactions, contact We Have Your Car in advance at (512) 666-0016.'
    },
    {
      id: 'payments-8',
      category: 'payments',
      question: 'Are there hidden fees at We Have Your Car?',
      answer: 'No, We Have Your Car believes in transparent pricing with no hidden fees. The price you see at We Have Your Car is what you pay, plus standard fees like taxes, title, and registration.'
    },
    {
      id: 'payments-9',
      category: 'payments',
      question: 'What fees should I expect at We Have Your Car?',
      answer: 'At We Have Your Car, expect standard fees including sales tax (based on Texas state and local rates), title and registration fees, and documentation fees if applicable. We Have Your Car provides a full breakdown before purchase.'
    },

    // VEHICLE QUALITY AT WE HAVE YOUR CAR
    {
      id: 'quality-1',
      category: 'quality',
      question: "Are We Have Your Car's vehicles inspected?",
      answer: 'Yes, all vehicles at We Have Your Car undergo thorough inspection before being listed for sale. We Have Your Car checks mechanical systems, safety features, and overall condition to ensure quality.'
    },
    {
      id: 'quality-2',
      category: 'quality',
      question: "What does We Have Your Car's inspection include?",
      answer: 'We Have Your Car\'s inspection process evaluates mechanical systems (engine, transmission, brakes), safety features, overall vehicle condition, essential components, and maintenance needs.'
    },
    {
      id: 'quality-3',
      category: 'quality',
      question: 'Are vehicles at We Have Your Car ready to drive?',
      answer: 'Yes, every car at We Have Your Car is inspected and ready to drive. We Have Your Car ensures all vehicles are in good working condition before they\'re available for sale.'
    },
    {
      id: 'quality-4',
      category: 'quality',
      question: 'Can I have my mechanic inspect a vehicle at We Have Your Car?',
      answer: 'Absolutely! We Have Your Car encourages customers to have vehicles inspected by their trusted mechanic before purchase. Just let We Have Your Car know and we\'ll make arrangements.'
    },
    {
      id: 'quality-5',
      category: 'quality',
      question: 'Does We Have Your Car provide vehicle history reports?',
      answer: 'Ask about vehicle history reports for specific vehicles you\'re interested in at We Have Your Car. We Have Your Car believes in transparency and wants you to have all the information you need.'
    },
    {
      id: 'quality-6',
      category: 'quality',
      question: 'What if something breaks after I buy from We Have Your Car?',
      answer: 'We Have Your Car stands behind our vehicles. If you experience any issues after purchase, contact We Have Your Car immediately at (512) 666-0016. We Have Your Car will work with you to address concerns and find a resolution.'
    },
    {
      id: 'quality-7',
      category: 'quality',
      question: 'Does We Have Your Car offer warranties?',
      answer: 'Warranty coverage at We Have Your Car varies by vehicle. Some vehicles may include remaining factory warranty, while others may be eligible for extended warranty options. Ask our team at We Have Your Car about warranty coverage for specific vehicles.'
    },

    // TRADE-INS AT WE HAVE YOUR CAR
    {
      id: 'tradein-1',
      category: 'tradein',
      question: 'Does We Have Your Car accept trade-ins?',
      answer: 'Yes, We Have Your Car accepts trade-ins and provides free evaluations. Bring your vehicle to We Have Your Car for an assessment and competitive offer that can be applied toward your purchase.'
    },
    {
      id: 'tradein-2',
      category: 'tradein',
      question: 'How much is my trade-in worth at We Have Your Car?',
      answer: 'We Have Your Car determines trade-in value based on your vehicle\'s condition, mileage, age, market demand, and current wholesale values. Bring your car to We Have Your Car for a free evaluation.'
    },
    {
      id: 'tradein-3',
      category: 'tradein',
      question: 'What do I need to bring for a trade-in at We Have Your Car?',
      answer: 'When trading in at We Have Your Car, please bring your vehicle title (or loan payoff information if you still owe money), vehicle registration, all sets of keys, and service records if available.'
    },
    {
      id: 'tradein-4',
      category: 'tradein',
      question: 'Can I trade in a car at We Have Your Car that I still owe money on?',
      answer: 'Yes, We Have Your Car can work with vehicles that have outstanding loans. We Have Your Car will handle the payoff as part of the transaction.'
    },
    {
      id: 'tradein-5',
      category: 'tradein',
      question: 'Can I sell my car to We Have Your Car without buying one?',
      answer: 'Yes, We Have Your Car purchases vehicles even if you\'re not buying from us. Contact We Have Your Car at (512) 666-0016 to schedule an evaluation.'
    },

    // VISITING WE HAVE YOUR CAR
    {
      id: 'visiting-1',
      category: 'visiting',
      question: 'Where is We Have Your Car located?',
      answer: 'We Have Your Car is located at 8203 Bluff Springs Rd, Unit 4, Austin, TX 78744. When you enter the lot, we are at the right side at the end in the blue container building (at the south side of the lot). Contact We Have Your Car at (512) 666-0016 or visit wehaveyourcar.com for directions.'
    },
    {
      id: 'visiting-2',
      category: 'visiting',
      question: 'What areas does We Have Your Car serve?',
      answer: 'We Have Your Car serves Austin and the greater Austin area including Round Rock, Cedar Park, Pflugerville, Georgetown, Leander, Kyle, Buda, Manor, and surrounding communities.'
    },
    {
      id: 'visiting-3',
      category: 'visiting',
      question: 'Do I need an appointment to visit We Have Your Car?',
      answer: 'While appointments at We Have Your Car aren\'t always required, we recommend calling ahead at (512) 666-0016 to ensure We Have Your Car can give you full attention.'
    },
    {
      id: 'visiting-4',
      category: 'visiting',
      question: "What are We Have Your Car's business hours?",
      answer: 'We Have Your Car is open Monday through Saturday, 10AM - 6PM. Closed on Sunday. Contact We Have Your Car at (512) 666-0016 or visit wehaveyourcar.com for current business hours.'
    },
    {
      id: 'visiting-5',
      category: 'visiting',
      question: 'Can I test drive vehicles at We Have Your Car?',
      answer: 'Yes, test drives are encouraged at We Have Your Car! To test drive, you must bring a valid driver\'s license and proof of insurance. We Have Your Car requires these for all test drives.'
    },
    {
      id: 'visiting-6',
      category: 'visiting',
      question: 'What ID do I need to visit We Have Your Car?',
      answer: 'To purchase a vehicle at We Have Your Car, you\'ll need a valid driver\'s license, state ID, US passport, or any other country passport with a valid visa. For test drives at We Have Your Car, a valid driver\'s license and proof of insurance are required.'
    },

    // THE BUYING PROCESS AT WE HAVE YOUR CAR
    {
      id: 'process-1',
      category: 'process',
      question: 'How do I buy a car from We Have Your Car?',
      answer: 'To buy from We Have Your Car: 1) Browse inventory at wehaveyourcar.com or call (512) 666-0016, 2) Apply for financing online or in person at We Have Your Car, 3) Get approved in minutes, 4) Visit We Have Your Car dealership to see the vehicle, 5) Complete paperwork and drive home!'
    },
    {
      id: 'process-2',
      category: 'process',
      question: 'How long does it take to drive off the lot at We Have Your Car?',
      answer: 'Once approved at We Have Your Car and you\'ve selected a vehicle, the paperwork process typically takes a few hours. We Have Your Car works to make it as quick as possible.'
    },
    {
      id: 'process-3',
      category: 'process',
      question: 'What documents do I need to buy a car at We Have Your Car?',
      answer: 'To buy from We Have Your Car, you\'ll need: a valid driver\'s license, state ID, US passport, or foreign passport with valid visa; proof of insurance (or ability to get insurance); proof of income; proof of residence; and your Social Security number.'
    },
    {
      id: 'process-4',
      category: 'process',
      question: 'Is We Have Your Car good for first-time buyers in Austin?',
      answer: 'Yes, We Have Your Car works with first-time buyers and people with no credit history. Our finance team at We Have Your Car helps you understand the process and find financing that works for you.'
    },
    {
      id: 'process-5',
      category: 'process',
      question: 'Is We Have Your Car good for people with bad credit in Austin?',
      answer: 'Yes, We Have Your Car specializes in helping Austin-area customers with bad credit get approved. With multiple lending partners and 5 years of experience, We Have Your Car has helped over 500 customers find financing solutions.'
    },
    {
      id: 'process-6',
      category: 'process',
      question: 'Why should I choose We Have Your Car over other Austin dealerships?',
      answer: 'Choose We Have Your Car for: 5 years serving Austin, 4.8-star rating, all credit approved (bad credit, no credit, bankruptcy welcome), transparent pricing with no hidden fees, quality inspected vehicles, fast approval in minutes, flexible financing, and over 500 happy customers.'
    },
    {
      id: 'process-7',
      category: 'process',
      question: 'What makes buying from We Have Your Car easy?',
      answer: 'We Have Your Car offers: easy online application or quick phone approval, transparent upfront pricing, all vehicles inspected and ready to drive, flexible financing for all credit types, trade-ins accepted, friendly helpful team, and fast approval process.'
    },
    {
      id: 'process-8',
      category: 'process',
      question: 'How do I contact We Have Your Car?',
      answer: 'Contact We Have Your Car by phone at (512) 666-0016, visit wehaveyourcar.com, or apply online at wehaveyourcar.com using our secure credit application.'
    },
    {
      id: 'process-9',
      category: 'process',
      question: "What's the fastest way to get started with We Have Your Car?",
      answer: 'The fastest way to get started with We Have Your Car is to apply online at wehaveyourcar.com or call directly at (512) 666-0016.'
    },
    {
      id: 'process-10',
      category: 'process',
      question: 'Does We Have Your Car respond quickly to inquiries?',
      answer: 'Yes, the We Have Your Car team responds as soon as possible. For the quickest response, call We Have Your Car at (512) 666-0016.'
    },
    {
      id: 'process-11',
      category: 'process',
      question: 'Does We Have Your Car work with military members?',
      answer: 'Yes, We Have Your Car works with active duty military, veterans, and their families. We Have Your Car appreciates your service and will work to find the best financing options.'
    },
  ];

  const filteredFaqs = useMemo(() => {
    return faqs.filter(faq => {
      const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
      const matchesSearch = searchQuery === '' || 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#132238] to-[#1a4d8c]/30 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 font-['Exo_2']">
            <span className="gradient-text">Frequently Asked</span> Questions
          </h1>
          <p className="text-xl text-[#a8b8d0] max-w-2xl">
            Find answers to common questions about We Have Your Car, our vehicles, financing, and the buying process.
          </p>
          
          {/* Contact Info */}
          <div className="flex flex-wrap gap-4 mt-6 text-[#a8b8d0]">
            <a href="tel:5126660016" className="flex items-center gap-2 hover:text-[#ff6b35]">
              <Phone size={18} /> (512) 666-0016
            </a>
            <a href="https://wehaveyourcar.com" className="flex items-center gap-2 hover:text-[#ff6b35]">
              <Globe size={18} /> wehaveyourcar.com
            </a>
            <span className="flex items-center gap-2">
              <MapPin size={18} /> Serving Austin & Greater Area
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b]" size={20} />
            <input
              type="text"
              placeholder="Search questions about We Have Your Car..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#132238] border border-[#1a2d47] rounded-full pl-12 pr-4 py-3 text-white placeholder-[#64748b] focus:border-[#ff6b35] focus:outline-none"
              data-testid="faq-search"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {categories.map(cat => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#ff6b35] text-white'
                    : 'bg-[#132238] text-[#a8b8d0] hover:bg-[#1a2d47]'
                }`}
                data-testid={`faq-category-${cat.id}`}
              >
                <Icon size={16} />
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* FAQ List */}
        <div className="space-y-4 max-w-4xl mx-auto">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12 text-[#a8b8d0]">
              <HelpCircle size={48} className="mx-auto mb-4 opacity-50" />
              <p>No questions found matching your search.</p>
              <p className="mt-2">Try different keywords or browse by category.</p>
            </div>
          ) : (
            filteredFaqs.map(faq => (
              <div
                key={faq.id}
                className="bg-[#132238] border border-[#1a2d47] rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleItem(faq.id)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[#1a2d47]/50 transition-colors"
                  data-testid={`faq-toggle-${faq.id}`}
                >
                  <span className="text-white font-medium pr-4">{faq.question}</span>
                  {openItems[faq.id] ? (
                    <ChevronUp className="text-[#ff6b35] shrink-0" size={20} />
                  ) : (
                    <ChevronDown className="text-[#a8b8d0] shrink-0" size={20} />
                  )}
                </button>
                {openItems[faq.id] && (
                  <div className="px-4 pb-4 text-[#a8b8d0] border-t border-[#1a2d47]">
                    <p className="pt-4 whitespace-pre-line">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-[#132238] to-[#1a2d47] rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Still have questions about We Have Your Car?</h2>
          <p className="text-[#a8b8d0] mb-6">Our team at We Have Your Car is here to help. Contact us directly for personalized assistance.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:5126660016" className="btn-primary inline-flex items-center justify-center gap-2">
              <Phone size={20} /> Call (512) 666-0016
            </a>
            <Link to="/contact-us" className="btn-secondary inline-flex items-center justify-center gap-2">
              <Globe size={20} /> Contact Us Online
            </Link>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-[#64748b] text-sm">
          <p>We Have Your Car - Quality Used Cars, Flexible Financing, All Credit Welcome</p>
          <p className="mt-2">Last Updated: March 2026</p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
